import React from 'react';
import { Star, Calendar, Camera, Edit, Trash2 } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { ListItemSkeleton } from '../../components/shared/ClientPageSkeleton';
import { useClientReviews, useDeleteReview } from '../../services/reviewService';

const ClientReviews = () => {
  const { data: reviewsResponse, isLoading, error } = useClientReviews({ per_page: 50 });
  const deleteReviewMutation = useDeleteReview();

  // Extract reviews from response
  const paginatedData = reviewsResponse?.data;
  const reviews = Array.isArray(paginatedData?.data) 
    ? paginatedData.data 
    : Array.isArray(paginatedData) 
    ? paginatedData 
    : [];

  const handleDeleteReview = async (id) => {
    if (window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
      try {
        await deleteReviewMutation.mutateAsync(id);
      } catch (error) {
        console.error('Failed to delete review:', error);
        alert('Failed to delete review. Please try again.');
      }
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
          i < rating
            ? 'text-black fill-current'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  if (isLoading) {
    return (
      <ClientPageLayout title="My Reviews">
        <div className="bg-white rounded-lg border border-black p-3 sm:p-4 md:p-6">
          <div className="h-5 sm:h-6 w-32 bg-gray-200 rounded mb-4 sm:mb-6 animate-pulse"></div>
          <ListItemSkeleton count={3} />
        </div>
      </ClientPageLayout>
    );
  }

  if (error) {
    return (
      <ClientPageLayout title="My Reviews">
        <div className="bg-white rounded-lg border border-black p-3 sm:p-4 md:p-6">
          <div className="text-center py-8 sm:py-12">
            <Star className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-gray-300" />
            <h3 className="text-lg sm:text-xl font-bold text-black mb-2">Error Loading Reviews</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">Failed to load your reviews. Please try again later.</p>
          </div>
        </div>
      </ClientPageLayout>
    );
  }

  return (
    <ClientPageLayout title="My Reviews">
      <div className="bg-white rounded-lg border border-black p-3 sm:p-4 md:p-6">
        {reviews.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <Star className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-gray-300" />
            <h3 className="text-lg sm:text-xl font-bold text-black mb-2">No Reviews Yet</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">You haven't left any reviews yet. Share your experience with vendors!</p>
            <a
              href="/bookings"
              className="inline-block bg-black text-white px-5 py-2 sm:px-6 sm:py-2.5 rounded-lg hover:bg-gray-900 transition-colors text-sm sm:text-base"
            >
              View My Bookings
            </a>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-black">
                {reviews.length} Review{reviews.length !== 1 ? 's' : ''}
              </h2>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              {reviews.map((review) => {
                const vendorName = review.booking?.vendor?.business_name || 
                                  review.vendor?.business_name || 
                                  'Unknown Vendor';
                const serviceName = review.booking?.vendorService?.service_name || 
                                  review.booking?.vendor_service?.service_name ||
                                  'Service';
                const eventDate = review.booking?.event_date || review.booking?.created_at;
                
                return (
                  <div
                    key={review.id}
                    className="border border-gray-200 rounded-lg p-3 sm:p-4 md:p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                          <Camera className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1 gap-1 sm:gap-0">
                            <h3 className="font-bold text-sm sm:text-base text-black truncate">
                              {vendorName}
                            </h3>
                            {review.is_verified && (
                              <span className="px-2 py-0.5 bg-black text-white text-xs rounded self-start sm:self-auto">
                                Verified
                              </span>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 truncate">
                            {serviceName}
                          </p>
                          {review.title && (
                            <h4 className="text-xs sm:text-sm font-semibold text-black mb-1 sm:mb-2 line-clamp-2">
                              {review.title}
                            </h4>
                          )}
                          <div className="flex items-center space-x-1 mb-2">
                            {renderStars(review.rating)}
                          </div>
                          {review.comment && (
                            <p className="text-xs sm:text-sm text-gray-700 mb-2 sm:mb-3 line-clamp-3 sm:line-clamp-none">
                              {review.comment}
                            </p>
                          )}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-1 sm:gap-0 text-xs text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 flex-shrink-0" />
                              <span className="truncate">Reviewed {new Date(review.created_at).toLocaleDateString()}</span>
                            </div>
                            {eventDate && (
                              <div className="flex items-center">
                                <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 flex-shrink-0" />
                                <span className="truncate">Event: {new Date(eventDate).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end sm:justify-start space-x-2 sm:ml-4 flex-shrink-0">
                        <button
                          className="p-1.5 sm:p-2 text-gray-400 hover:text-blue-500 transition-colors active:scale-95"
                          title="Edit review"
                        >
                          <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteReview(review.id)}
                          disabled={deleteReviewMutation.isPending}
                          className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 active:scale-95"
                          title="Delete review"
                        >
                          <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ClientPageLayout>
  );
};

export default ClientReviews;

